import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import '../styles/marketing.css';
import '../styles/legal.css';

const EFFECTIVE_DATE = 'March 15, 2026';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'description', title: 'Description of Service' },
  { id: 'accounts', title: 'Accounts & Registration' },
  { id: 'acceptable-use', title: 'Acceptable Use' },
  { id: 'clinical-data', title: 'Clinical Data & Disclaimers' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'privacy', title: 'Privacy' },
  { id: 'third-party', title: 'Third-Party Services' },
  { id: 'availability', title: 'Service Availability' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'termination', title: 'Termination' },
  { id: 'contact', title: 'Contact Information' },
];

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <MarketingNav />

      <section className="legal-hero">
        <div className="legal-hero-badge">
          <FileText size={14} />
          Effective {EFFECTIVE_DATE}
        </div>
        <h1>Terms of Service</h1>
        <p className="legal-hero-subtitle">
          The terms and conditions governing your use of the Amplify by
          CareTracker platform.
        </p>
      </section>

      <div className="legal-layout">
        <nav className="legal-toc">
          <h3>Contents</h3>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal-content">
          {/* 1. Acceptance */}
          <section id="acceptance" className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms of Service (&quot;Terms&quot;) constitute a legally
              binding agreement between you (&quot;User,&quot; &quot;you,&quot;
              or &quot;your&quot;) and Harris CareTracker, a division of Harris
              Computer Systems (&quot;Company,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;), governing your access to and
              use of the Amplify by CareTracker platform (the
              &quot;Service&quot;).
            </p>
            <p>
              By creating an account, accessing, or using the Service, you
              acknowledge that you have read, understood, and agree to be bound
              by these Terms, our{' '}
              <Link to="/privacy">Privacy Policy</Link>, and our{' '}
              <Link to="/eula">End User License Agreement (EULA)</Link>. If you
              are using the Service on behalf of a healthcare organization, you
              represent that you have authority to bind that organization to
              these Terms.
            </p>
            <p>
              If you do not agree with any part of these Terms, you must not
              access or use the Service.
            </p>
          </section>

          {/* 2. Description of Service */}
          <section id="description" className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Amplify by CareTracker is a HIPAA-compliant, medical-grade AI
              platform that provides:
            </p>
            <ul>
              <li>
                Ambient clinical recording and AI-powered transcription of
                medical appointments
              </li>
              <li>
                Automated clinical summarization with structured medical
                categories (chief complaint, HPI, review of systems, physical
                exam, assessment and plan)
              </li>
              <li>
                AI-assisted diagnostic coding (ICD-10) and procedure coding
                (CPT) with evidence-based recommendations
              </li>
              <li>
                Medical note generation for physician review, validation, and
                electronic signing
              </li>
              <li>
                Integration with Electronic Health Record (EHR) systems for
                bi-directional clinical data exchange
              </li>
            </ul>
            <p>
              The Service is available as a web application and as native mobile
              applications for iOS and Android.
            </p>
          </section>

          {/* 3. Accounts */}
          <section id="accounts" className="legal-section">
            <h2>3. Accounts & Registration</h2>

            <h3>3.1 Account Creation</h3>
            <p>
              Access to the Service requires an account. Accounts are created
              through organizational invitation by an administrator. You may not
              create an account without an authorized invitation from a
              subscribing healthcare organization.
            </p>

            <h3>3.2 Account Responsibilities</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>
                Maintaining the confidentiality of your login credentials
              </li>
              <li>
                All activities that occur under your account
              </li>
              <li>
                Immediately notifying your organization&apos;s administrator of
                any unauthorized use of your account
              </li>
              <li>
                Ensuring that your professional credentials on file are current
                and accurate
              </li>
            </ul>

            <h3>3.3 Professional Qualifications</h3>
            <p>
              Certain features of the Service (such as clinical validation, code
              approval, and note signing) require appropriate professional
              credentials. You represent that all professional information
              provided is accurate and that you hold valid licensure as required
              by applicable law.
            </p>
          </section>

          {/* 4. Acceptable Use */}
          <section id="acceptable-use" className="legal-section">
            <h2>4. Acceptable Use Policy</h2>
            <p>You agree to use the Service only for its intended purpose of supporting clinical documentation workflows. You shall not:</p>
            <ul>
              <li>
                Use the Service for any unlawful purpose or in violation of any
                applicable law or regulation, including HIPAA
              </li>
              <li>
                Access or attempt to access data belonging to other
                organizations or users
              </li>
              <li>
                Share your account credentials with unauthorized individuals
              </li>
              <li>
                Interfere with, disrupt, or attempt to gain unauthorized access
                to the Service or its infrastructure
              </li>
              <li>
                Reverse engineer, decompile, or disassemble any component of the
                Service
              </li>
              <li>
                Use the Service to record individuals without their knowledge or
                consent as required by applicable law
              </li>
              <li>
                Transmit malicious code, viruses, or any harmful technology
              </li>
              <li>
                Use automated scripts, bots, or scrapers to access the Service
              </li>
              <li>
                Resell, sublicense, or provide the Service to third parties
                without authorization
              </li>
            </ul>
          </section>

          {/* 5. Clinical Data */}
          <section id="clinical-data" className="legal-section">
            <h2>5. Clinical Data & Medical Disclaimers</h2>

            <h3>5.1 AI-Assisted, Not AI-Directed</h3>
            <div className="legal-info-box">
              <p>
                <strong>Important:</strong> The Service provides AI-assisted
                clinical documentation tools. All AI-generated content
                (transcriptions, clinical summaries, diagnostic codes, and
                medical notes) is preliminary and requires review, validation,
                and approval by a qualified healthcare professional before use in
                patient care.
              </p>
            </div>

            <h3>5.2 No Medical Advice</h3>
            <p>
              The Service does not provide medical advice, diagnosis, or
              treatment recommendations. The AI-generated content is intended to
              assist with clinical documentation and coding workflows only.
              Clinical decision-making remains the sole responsibility of the
              treating physician or healthcare provider.
            </p>

            <h3>5.3 Physician Review Required</h3>
            <p>
              All clinical data processed by the Service follows a mandatory
              physician review workflow. AI-generated transcriptions, summaries,
              and codes are not considered final until explicitly reviewed and
              approved by an authorized healthcare professional. The Service
              enforces this through its clinical workflow state machine, which
              requires explicit validation at each stage.
            </p>

            <h3>5.4 Data Ownership</h3>
            <p>
              Your healthcare organization retains full ownership of all clinical
              data processed through the Service. We claim no ownership rights
              over your patient data, transcriptions, clinical notes, or any
              other content you create or process using the Service.
            </p>

            <h3>5.5 Recording Consent</h3>
            <p>
              You are solely responsible for obtaining all necessary consents
              from patients and other participants before recording clinical
              appointments. Consent requirements vary by jurisdiction, and you
              must comply with all applicable federal, state, and local laws
              regarding audio recording consent.
            </p>
          </section>

          {/* 6. Intellectual Property */}
          <section id="intellectual-property" className="legal-section">
            <h2>6. Intellectual Property</h2>
            <p>
              The Service, including its software, design, algorithms, user
              interface, documentation, and all related intellectual property, is
              owned by Harris CareTracker and protected by copyright, trademark,
              and other intellectual property laws.
            </p>
            <p>
              Subject to your compliance with these Terms, we grant you a
              limited, non-exclusive, non-transferable, revocable license to
              access and use the Service for its intended purpose during the term
              of your subscription. This license does not include the right to
              modify, distribute, or create derivative works based on the
              Service.
            </p>
            <p>
              &quot;Amplify,&quot; &quot;CareTracker,&quot; and &quot;Amplify by
              CareTracker&quot; are trademarks of Harris CareTracker. You may
              not use these marks without our prior written consent.
            </p>
          </section>

          {/* 7. Privacy */}
          <section id="privacy" className="legal-section">
            <h2>7. Privacy</h2>
            <p>
              Your use of the Service is also governed by our{' '}
              <Link to="/privacy">Privacy Policy</Link>, which describes how we
              collect, use, and protect your personal and clinical information.
              The Privacy Policy is incorporated into these Terms by reference.
            </p>
            <p>
              For healthcare organizations processing Protected Health
              Information (PHI) through the Service, a Business Associate
              Agreement (BAA) governs the handling of PHI in compliance with
              HIPAA. The BAA is a separate agreement executed between your
              organization and Harris CareTracker.
            </p>
          </section>

          {/* 8. Third-Party Services */}
          <section id="third-party" className="legal-section">
            <h2>8. Third-Party Services</h2>
            <p>
              The Service integrates with third-party services to provide its
              functionality. These include, but are not limited to:
            </p>
            <ul>
              <li>
                <strong>Microsoft Azure:</strong> Cloud infrastructure, AI
                services (Speech-to-Text, OpenAI, Language services), and data
                storage
              </li>
              <li>
                <strong>IMO Health:</strong> Medical terminology and clinical
                coding validation
              </li>
              <li>
                <strong>EHR Systems:</strong> Electronic Health Record
                integration when configured by your organization
              </li>
            </ul>
            <p>
              Your use of these third-party services through the Service is
              subject to their respective terms of service and privacy policies.
              We are not responsible for the practices of third-party service
              providers, though we maintain appropriate data processing
              agreements with each.
            </p>
          </section>

          {/* 9. Service Availability */}
          <section id="availability" className="legal-section">
            <h2>9. Service Availability & Modifications</h2>

            <h3>9.1 Availability</h3>
            <p>
              We strive to maintain high availability of the Service but do not
              guarantee uninterrupted access. The Service may be temporarily
              unavailable due to scheduled maintenance, updates, or
              circumstances beyond our control.
            </p>

            <h3>9.2 Modifications</h3>
            <p>
              We reserve the right to modify, update, or discontinue features of
              the Service at any time. We will provide reasonable notice of
              material changes that affect your use of the Service. If a change
              materially reduces the functionality available under your
              subscription, you may terminate your subscription as described in
              Section 13.
            </p>
          </section>

          {/* 10. Limitation of Liability */}
          <section id="liability" className="legal-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL HARRIS CARETRACKER, ITS AFFILIATES, OFFICERS, DIRECTORS,
              EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
              PROFITS, REVENUE, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR
              USE OF THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT
              (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL
              THEORY.
            </p>
            <p>
              OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR
              RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE TOTAL
              AMOUNT PAID BY YOUR ORGANIZATION TO US IN THE TWELVE (12) MONTHS
              PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>
            <p>
              THE SERVICE PROVIDES AI-ASSISTED TOOLS FOR CLINICAL DOCUMENTATION.
              WE ARE NOT LIABLE FOR CLINICAL DECISIONS MADE BASED ON
              AI-GENERATED CONTENT. ALL CLINICAL CONTENT REQUIRES INDEPENDENT
              PROFESSIONAL REVIEW AND VALIDATION.
            </p>
          </section>

          {/* 11. Indemnification */}
          <section id="indemnification" className="legal-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Harris
              CareTracker, its parent company (Harris Computer Systems /
              Constellation Software), affiliates, officers, directors,
              employees, and agents from and against any claims, liabilities,
              damages, losses, costs, and expenses (including reasonable
              attorneys&apos; fees) arising out of or relating to:
            </p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>
                Your violation of any applicable law, regulation, or third-party
                right (including HIPAA)
              </li>
              <li>
                Your failure to obtain required consents for clinical recording
              </li>
              <li>
                Clinical decisions made based on AI-generated content without
                appropriate professional review
              </li>
            </ul>
          </section>

          {/* 12. Governing Law */}
          <section id="governing-law" className="legal-section">
            <h2>12. Governing Law & Dispute Resolution</h2>

            <h3>12.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of New York, without regard to its conflict
              of laws principles.
            </p>

            <h3>12.2 Dispute Resolution</h3>
            <p>
              Any dispute arising out of or relating to these Terms or the
              Service shall first be attempted to be resolved through good-faith
              negotiation between the parties. If the dispute cannot be resolved
              within thirty (30) days, either party may initiate binding
              arbitration under the rules of the American Arbitration
              Association. The arbitration shall take place in Niagara Falls, New
              York, and shall be conducted in English.
            </p>

            <h3>12.3 Class Action Waiver</h3>
            <p>
              You agree that any dispute resolution proceeding will be conducted
              on an individual basis and not as a class, consolidated, or
              representative action.
            </p>
          </section>

          {/* 13. Termination */}
          <section id="termination" className="legal-section">
            <h2>13. Termination</h2>

            <h3>13.1 By You</h3>
            <p>
              You may terminate your account at any time by contacting your
              organization&apos;s administrator. Organization administrators may
              terminate their subscription by providing written notice as
              specified in their service agreement.
            </p>

            <h3>13.2 By Us</h3>
            <p>
              We may suspend or terminate your access to the Service immediately
              if you violate these Terms, engage in conduct that threatens the
              security of the Service or other users, or if required by law. For
              non-critical violations, we will provide reasonable notice and
              opportunity to cure before termination.
            </p>

            <h3>13.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service ceases
              immediately. Your organization may request export of its clinical
              data within 90 days of termination. After this period, data will be
              deleted in accordance with our{' '}
              <Link to="/privacy">Privacy Policy</Link> and applicable retention
              requirements.
            </p>
          </section>

          {/* 14. Contact */}
          <section id="contact" className="legal-section">
            <h2>14. Contact Information</h2>
            <p>
              For questions about these Terms or the Service, please contact us:
            </p>
            <div className="legal-contact-box">
              <h3>Harris CareTracker — Amplify Legal</h3>
              <p>
                <strong>Address:</strong> 2429 Military Rd, Suite 300, Niagara
                Falls, NY 14304
              </p>
              <p>
                <strong>Phone:</strong>{' '}
                <a href="tel:+18555284357">(855) 528-4357</a>
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:sales@harriscaretracker.com">
                  sales@harriscaretracker.com
                </a>
              </p>
              <p>
                <strong>Website:</strong>{' '}
                <a
                  href="https://harriscaretracker.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  harriscaretracker.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
};

export default TermsOfService;
